import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { EchoProfileCard } from '../EchoProfileCard';
import { DraftTestChatCard } from '../DraftTestChatCard';
import { RetrainEcho } from '../RetrainEcho';
import { VisibilityModal } from '../VisibilityModal';
import { Button } from '@/components/ui/Button';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import type { EchoDraftResponse } from '@/types/echo_creation';

import echoCreatedImage from '@/assets/images/webp/echo_created.webp';
import waveContactListSvg from '@/assets/images/svg/wave_element_contact_list.svg';

interface TestingStateProps {
  draft: EchoDraftResponse;
  onRetrain: () => void;
  onSaveEcho: (isPublic: boolean) => Promise<void>;
  isSaving: boolean;
}

export const TestingState: React.FC<TestingStateProps> = ({
  draft,
  onRetrain,
  onSaveEcho,
  isSaving,
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleSave = async (isPublic: boolean) => {
    try {
      await onSaveEcho(isPublic);
      setIsModalOpen(false);
    } catch {
      // Error already handled by useMyEcho toast
    }
  };

  const handleBack = () => {
    navigate(`/create-echo?draftId=${encodeURIComponent(draft.id)}`);
  };

  return (
    <div className='relative size-full overflow-hidden bg-white'>
      <img
        src={waveContactListSvg}
        alt=''
        className='pointer-events-none absolute bottom-0 left-0 z-0 select-none'
      />

      <div className='custom-scrollbar relative z-10 size-full overflow-y-auto p-6 md:p-10'>
        <div className='mb-1 flex w-full shrink-0 flex-col gap-4 md:mb-4 md:flex-row md:items-center md:justify-between'>
          <div className='flex items-center gap-3'>
            <button
              type='button'
              onClick={handleBack}
              className='rounded-full p-1 transition-colors hover:bg-gray-100'
              aria-label='Go back to personality config'
            >
              <ArrowLeftIcon className='size-6 text-dark-100' strokeWidth={2.5} />
            </button>
            <h1 className='whitespace-nowrap font-proxima text-24 font-extrabold uppercase leading-47 tracking-normal text-brand-800 md:text-40 md:leading-47'>
              ECHO PROFILE
            </h1>
          </div>
          <div className='hidden items-center gap-4 xl:flex'>
            <Button
              variant='purple-dark'
              onClick={() => setIsModalOpen(true)}
              disabled={isSaving}
              className='h-[40px] px-6 text-14 font-bold uppercase tracking-wide'
            >
              {isSaving ? 'SAVING...' : 'SAVE THIS ECHO'}
            </Button>
          </div>
        </div>

        <div className='mx-0 w-full max-w-1215 wide:mx-auto'>
          <div className='flex w-full flex-col gap-12'>
            {/* Top Grid: Profile + Test Chat */}
            <div className='flex flex-col gap-6 lg:grid lg:grid-cols-12 lg:gap-6'>
              <div className='flex flex-col gap-6 lg:col-span-5'>
                <EchoProfileCard
                  name={draft.name || 'My Echo'}
                  imageSrc={draft.profile_image_url ?? echoCreatedImage}
                  idleVideoUrl={draft.idle_video_url}
                  draftId={draft.id}
                />

                {/* Desktop: Retrain */}
                <div className='hidden lg:block'>
                  <RetrainEcho onRetrain={onRetrain} />
                </div>
              </div>

              <div className='lg:relative lg:col-span-7'>
                <div className='-mx-6 w-[calc(100%+48px)] md:mx-0 md:w-full lg:absolute lg:inset-0 lg:size-full'>
                  <DraftTestChatCard draft={draft} />
                </div>

                {/* Mobile: Retrain */}
                <div className='mt-6 block lg:hidden'>
                  <RetrainEcho onRetrain={onRetrain} />
                </div>
              </div>
            </div>

            {/* Mobile: Save Button (header button is desktop-only via xl:block) */}
            <div className='w-full xl:hidden'>
              <Button
                variant='purple-dark'
                onClick={() => setIsModalOpen(true)}
                disabled={isSaving}
                className='h-[48px] w-full text-16 font-bold uppercase tracking-wide'
              >
                {isSaving ? 'SAVING...' : 'SAVE THIS ECHO'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <VisibilityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </div>
  );
};
